/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from "react";
import {
  Flag,
  CheckCircle,
  XCircle,
  User as UserIcon,
  Newspaper,
  Shield,
  Users as UsersIcon,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";

interface AbuseReport {
  id: number;
  reporterId: number;
  targetType: "user" | "story" | "team" | "league";
  targetId: number;
  reason: string;
  details: string | null;
  status: "pending" | "actioned" | "dismissed";
  reviewedById: number | null;
  reviewedAt: string | null;
  createdAt: string;
  reporter: { id: number; name: string; email: string };
}

const REASON_LABELS: Record<string, string> = {
  offensive_name: "Offensive name",
  offensive_avatar: "Inappropriate avatar",
  hate_speech: "Hate speech / harassment",
  spam: "Spam or misleading",
  impersonation: "Impersonation",
  other: "Other",
};

const TARGET_ICONS: Record<string, React.ElementType> = {
  user: UserIcon,
  story: Newspaper,
  team: Shield,
  league: UsersIcon,
};

const TARGET_COLORS: Record<string, string> = {
  user: "bg-blue-50 text-blue-700 border-blue-200",
  story: "bg-purple-50 text-purple-700 border-purple-200",
  team: "bg-emerald-50 text-emerald-700 border-emerald-200",
  league: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function AbuseReportsPage() {
  const queryClient = useQueryClient();
  const [targetFilter, setTargetFilter] = useState<string>("all");
  const [reasonFilter, setReasonFilter] = useState<string>("all");

  const { data: reports = [], isLoading } = useQuery<AbuseReport[]>({
    queryKey: ["abuse-reports"],
    queryFn: () => apiClient.get(API_ENDPOINTS.ABUSE_REPORTS.LIST),
    // Shape-coerce: a bad server response never crashes the page.
    select: (r: any) => (Array.isArray(r) ? (r as AbuseReport[]) : []),
    refetchInterval: 60_000, // fresh every minute — reports are latency-sensitive
  });

  const resolve = useMutation({
    mutationFn: ({
      id,
      action,
    }: {
      id: number;
      action: "actioned" | "dismissed";
    }) =>
      apiClient.patch(API_ENDPOINTS.ABUSE_REPORTS.RESOLVE(id), { action }),
    onSuccess: (_data, vars) => {
      toast.success(
        vars.action === "actioned"
          ? "Report actioned — user warned/banned"
          : "Report dismissed",
      );
      queryClient.invalidateQueries({ queryKey: ["abuse-reports"] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Could not resolve report");
    },
  });

  const filtered = useMemo(() => {
    return reports.filter((r) => {
      if (targetFilter !== "all" && r.targetType !== targetFilter) return false;
      if (reasonFilter !== "all" && r.reason !== reasonFilter) return false;
      return true;
    });
  }, [reports, targetFilter, reasonFilter]);

  const oldestAge = useMemo(() => {
    if (filtered.length === 0) return null;
    const oldest = filtered.reduce((a, b) =>
      new Date(a.createdAt) < new Date(b.createdAt) ? a : b,
    );
    const ageMs = Date.now() - new Date(oldest.createdAt).getTime();
    const hours = Math.floor(ageMs / 3600_000);
    return hours;
  }, [filtered]);

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Flag className="h-6 w-6 text-rose-600" />
            Abuse Reports
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            User-submitted reports of offensive names, avatars, stories, teams,
            and leagues. Apple requires action on valid reports within 24 hours
            — this queue is your audit trail.
          </p>
        </div>

        {/* Oldest-report SLA pill */}
        {oldestAge !== null && (
          <div
            className={`rounded-xl px-4 py-2.5 text-sm font-semibold border ${
              oldestAge >= 24
                ? "bg-rose-50 text-rose-700 border-rose-200"
                : oldestAge >= 12
                  ? "bg-amber-50 text-amber-700 border-amber-200"
                  : "bg-emerald-50 text-emerald-700 border-emerald-200"
            }`}
          >
            Oldest pending: {oldestAge}h
            {oldestAge >= 24 && " — over SLA"}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <Select value={targetFilter} onValueChange={setTargetFilter}>
          <SelectTrigger className="w-[180px] bg-white">
            <SelectValue placeholder="Target type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All targets</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="story">Story</SelectItem>
            <SelectItem value="team">Team</SelectItem>
            <SelectItem value="league">League</SelectItem>
          </SelectContent>
        </Select>

        <Select value={reasonFilter} onValueChange={setReasonFilter}>
          <SelectTrigger className="w-[220px] bg-white">
            <SelectValue placeholder="Reason" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All reasons</SelectItem>
            {Object.entries(REASON_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto text-xs text-slate-500">
          {filtered.length} pending
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="py-20 text-center text-slate-500 text-sm">
          Loading reports…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-50 mx-auto mb-3 flex items-center justify-center">
            <CheckCircle className="h-8 w-8 text-emerald-500" />
          </div>
          <h3 className="font-bold text-slate-900">All clear</h3>
          <p className="text-sm text-slate-500 mt-1">
            No pending reports right now. Nice.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const Icon = TARGET_ICONS[r.targetType] ?? Flag;
            const ageMs = Date.now() - new Date(r.createdAt).getTime();
            const ageHours = Math.floor(ageMs / 3600_000);
            const overSla = ageHours >= 24;

            return (
              <div
                key={r.id}
                className={`bg-white border rounded-xl p-4 hover:shadow-sm transition ${
                  overSla ? "border-rose-200" : "border-slate-200"
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Target badge */}
                  <div
                    className={`shrink-0 w-11 h-11 rounded-lg border flex items-center justify-center ${TARGET_COLORS[r.targetType] ?? "bg-slate-50 text-slate-600 border-slate-200"}`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>

                  {/* Main body */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold uppercase tracking-wide text-slate-700">
                        {r.targetType} #{r.targetId}
                      </span>
                      <Badge variant="outline" className="text-[11px]">
                        {REASON_LABELS[r.reason] ?? r.reason}
                      </Badge>
                      <span
                        className={`text-[11px] font-semibold ${overSla ? "text-rose-600" : "text-slate-400"}`}
                      >
                        {ageHours < 1
                          ? `${Math.floor(ageMs / 60000)}m ago`
                          : `${ageHours}h ago`}
                        {overSla && " • over SLA"}
                      </span>
                    </div>
                    {r.details && (
                      <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                        {r.details}
                      </p>
                    )}
                    <p className="text-xs text-slate-500 mt-2">
                      Reported by{" "}
                      <span className="font-semibold text-slate-700">
                        {r.reporter.name}
                      </span>{" "}
                      ({r.reporter.email})
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button
                      size="sm"
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                      onClick={() =>
                        resolve.mutate({ id: r.id, action: "actioned" })
                      }
                      disabled={resolve.isPending}
                    >
                      <CheckCircle className="h-3.5 w-3.5 mr-1" />
                      Action
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        resolve.mutate({ id: r.id, action: "dismissed" })
                      }
                      disabled={resolve.isPending}
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1" />
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer guidance */}
      <div className="mt-8 p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600 leading-relaxed">
        <strong className="text-slate-900">How to handle reports:</strong>{" "}
        Open the target (click on the target badge, or navigate manually).
        If the content is clearly offensive or violates the rules, take
        action (rename the user, remove the story, delete the league) and
        mark this report <em>Action</em>. If the report is unfounded or
        retaliatory, mark <em>Dismiss</em>. Either way — respond within 24h
        to stay compliant with Apple Guideline 1.2 and Google's UGC policy.
      </div>
    </Layout>
  );
}
